/**
 * AddProductPage Component - Story 6.3: Create Add Product Form
 * Main page component for creating new product listings
 */

import React, { useEffect } from 'react';
import { SellerDashboardLayout } from '../components/SellerDashboardLayout';
import { ProductForm } from '../components/ProductForm/ProductForm';
import './AddProductPage.css';

/**
 * Props interface for AddProductPage component
 */
export interface AddProductPageProps {
  /** Optional callback when a product is successfully created */
  onProductCreated?: (productId: string) => void;
  /** When true, renders without SellerDashboardLayout wrapper (for embedding in other layouts) */
  disableLayout?: boolean;
}

/**
 * AddProductPage Component
 * Renders the add product form within the seller dashboard layout
 *
 * @component
 * @example
 * <AddProductPage onProductCreated={(id) => console.log('Created:', id)} />
 */
export const AddProductPage: React.FC<AddProductPageProps> = ({
  onProductCreated,
  disableLayout = false,
}) => {
  // Set page title and meta description on mount
  useEffect(() => {
    document.title = 'Add Product | SAUBHAGYA Seller Dashboard';

    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Create a new product listing on the SAUBHAGYA marketplace'
      );
    }
  }, []);

  /**
   * Handle successful product creation
   * @param productId - The ID of the newly created product
   */
  const handleProductCreated = (productId: string) => {
    console.log('[AddProductPage] Product created successfully:', productId);

    // Call optional callback if provided
    if (onProductCreated) {
      onProductCreated(productId);
    }

    // Navigation is handled by ProductForm component
  };

  const pageContent = (
    <div className="add-product-page" data-testid="add-product-page">
      <ProductForm onSuccess={handleProductCreated} />
    </div>
  );

  // Conditionally wrap content with SellerDashboardLayout
  if (disableLayout) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Add New Product</h2>
          <p className="text-gray-600 text-sm mt-1">Create a new product listing for your store</p>
        </div>
        {pageContent}
      </div>
    );
  }

  return (
    <SellerDashboardLayout
      activeNavItemId="add-product"
      breadcrumbs={[
        { label: 'Dashboard', path: '/marketplace/seller/dashboard' },
        { label: 'My Products', path: '/marketplace/seller/products' },
        { label: 'Add Product' },
      ]}
      hideStats={true}
      pageTitle="Add New Product"
      pageSubtitle="Create a new product listing for your store"
    >
      {pageContent}
    </SellerDashboardLayout>
  );
};

export default AddProductPage;
